<template>
  <div class="student-dashboard">
    <!-- 顶部统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon error-icon">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ totalErrorQuestions }}</div>
          <div class="stat-label">错题总数</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon progress-icon">
          <i class="fa fa-chart-line"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ currentScore }}分</div>
          <div class="stat-label">当前成绩</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon target-icon">
          <i class="fa fa-bullseye"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ targetScore }}分</div>
          <div class="stat-label">目标成绩</div>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon study-icon">
          <i class="fa fa-calendar-check"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ studyDays }}天</div>
          <div class="stat-label">连续学习</div>
        </div>
      </div>
    </div>

    <!-- 主要功能区域 -->
    <div class="main-content">
      <!-- 左侧功能面板 -->
      <div class="left-panel">
        <!-- 学习进度 -->
        <div class="progress-section">
          <div class="section-header">
            <h3>学习进度</h3>
            <span class="progress-percentage">{{ progressPercentage }}%</span>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
          </div>
          <div class="progress-info">
            <span>距离目标还需 {{ remainingDays }} 天</span>
          </div>
        </div>

        <!-- 今日任务 -->
        <div class="today-tasks">
          <div class="section-header">
            <h3>今日任务</h3>
            <span class="task-count">{{ completedTasks }}/{{ totalTasks }}</span>
          </div>
          <div class="task-list">
            <div 
              v-for="task in todayTasks" 
              :key="task.id"
              class="task-item"
              :class="{ completed: task.completed }"
              @click="toggleTask(task)"
            >
              <div class="task-checkbox">
                <i class="fa" :class="task.completed ? 'fa-check-circle' : 'fa-circle-o'"></i>
              </div>
              <div class="task-content">
                <div class="task-title">{{ task.title }}</div>
                <div class="task-desc">{{ task.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 薄弱知识点 -->
        <div class="weak-points">
          <div class="section-header">
            <h3>薄弱知识点</h3>
            <router-link to="/student/analysis" class="view-all">查看全部</router-link>
          </div>
          <div class="weak-point-list">
            <div 
              v-for="point in weakPoints" 
              :key="point.id"
              class="weak-point-item"
            >
              <div class="point-name">{{ point.name }}</div>
              <div class="point-accuracy">
                <span class="accuracy-text">正确率</span>
                <div class="accuracy-bar">
                  <div class="accuracy-fill" :style="{ width: point.accuracy + '%' }"></div>
                </div>
                <span class="accuracy-value">{{ point.accuracy }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧功能面板 -->
      <div class="right-panel">
        <!-- 快速功能 -->
        <div class="quick-actions">
          <div class="section-header">
            <h3>快速功能</h3>
          </div>
          <div class="action-grid">
            <div class="action-card" @click="goToErrorBook">
              <div class="action-icon error-book-icon">
                <i class="fa fa-book"></i>
              </div>
              <div class="action-title">错题本</div>
              <div class="action-desc">查看和复习错题</div>
            </div>
            
            <div class="action-card" @click="goToPractice">
              <div class="action-icon practice-icon">
                <i class="fa fa-pencil-alt"></i>
              </div>
              <div class="action-title">专项练习</div>
              <div class="action-desc">针对性训练</div>
            </div>
            
            <div class="action-card" @click="goToPlanning">
              <div class="action-icon planning-icon">
                <i class="fa fa-calendar"></i>
              </div>
              <div class="action-title">学习规划</div>
              <div class="action-desc">制定学习计划</div>
            </div>
            
            <div class="action-card" @click="goToPrint">
              <div class="action-icon print-icon">
                <i class="fa fa-print"></i>
              </div>
              <div class="action-title">实时对答</div>
              <div class="action-desc">实时解答问题</div>
            </div>
          </div>
        </div>

        <!-- 最近错题 -->
        <div class="recent-errors">
          <div class="section-header">
            <h3>最近错题</h3>
            <router-link to="/student/error-book" class="view-all">查看全部</router-link>
          </div>
          <div class="error-list">
            <div 
              v-for="error in recentErrors" 
              :key="error.id"
              class="error-item"
              @click="viewErrorDetail(error)"
            >
              <div class="error-image">
                <img :src="error.imageUrl" :alt="error.subject" />
              </div>
              <div class="error-content">
                <div class="error-subject">{{ error.subject }}</div>
                <div class="error-knowledge">{{ error.knowledgePoint }}</div>
                <div class="error-time">{{ formatTime(error.createTime) }}</div>
              </div>
              <div class="error-status" :class="error.status">
                {{ error.status === 'mastered' ? '已掌握' : '未掌握' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';

export default {
  name: 'StudentDashboard',
  data() {
    return {
      // 统计数据
      totalErrorQuestions: 0,
      currentScore: 0,
      targetScore: 0,
      studyDays: 0,
      
      // 进度数据
      progressPercentage: 0,
      remainingDays: 0,
      
      // 今日任务
      todayTasks: [],
      completedTasks: 0,
      totalTasks: 0,
      
      // 薄弱知识点
      weakPoints: [],
      
      // 最近错题
      recentErrors: []
    }
  },
  created() {
    this.loadDashboardData();
  },
  methods: {
    async loadDashboardData() {
      try {
        // 加载统计数据
        await this.loadStatistics();
        // 加载学习进度
        await this.loadProgress();
        // 加载今日任务
        await this.loadTodayTasks();
        // 加载薄弱知识点
        await this.loadWeakPoints();
        // 加载最近错题
        await this.loadRecentErrors();
      } catch (error) {
        console.error('加载仪表板数据失败:', error);
        this.$message.error('加载数据失败，请刷新重试');
      }
    },
    
    async loadStatistics() {
      try {
        const response = await axios.get('/student/statistics');
        const data = response.data.data;
        this.totalErrorQuestions = data.totalErrorQuestions || 0;
        this.currentScore = data.currentScore || 0;
        this.targetScore = data.targetScore || 90;
        this.studyDays = data.studyDays || 0;
      } catch (error) {
        console.error('加载统计数据失败:', error);
        // 使用默认数据
        this.totalErrorQuestions = 25;
        this.currentScore = 78;
        this.targetScore = 90;
        this.studyDays = 15;
      }
    },
    
    async loadProgress() {
      try {
        const response = await axios.get('/student/progress');
        const data = response.data.data;
        this.progressPercentage = data.progressPercentage || 0;
        this.remainingDays = data.remainingDays || 0;
      } catch (error) {
        console.error('加载进度数据失败:', error);
        this.progressPercentage = 65;
        this.remainingDays = 25;
      }
    },
    
    async loadTodayTasks() {
      try {
        const response = await axios.get('/student/today-tasks');
        const data = response.data.data;
        this.todayTasks = data.tasks || [];
        this.totalTasks = this.todayTasks.length;
        this.completedTasks = this.todayTasks.filter(task => task.completed).length;
      } catch (error) {
        console.error('加载今日任务失败:', error);
        // 使用默认任务数据
        this.todayTasks = [
          { id: '1', title: '完成数学练习', description: '函数专题', completed: true },
          { id: '2', title: '背诵英语单词', description: '50个单词', completed: false },
          { id: '3', title: '复习物理知识', description: '电学部分', completed: false }
        ];
        this.totalTasks = this.todayTasks.length;
        this.completedTasks = this.todayTasks.filter(task => task.completed).length;
      }
    },
    
    async loadWeakPoints() {
      try {
        const response = await axios.get('/student/weak-points');
        const data = response.data.data;
        this.weakPoints = data.weakPoints || [];
      } catch (error) {
        console.error('加载薄弱知识点失败:', error);
        this.weakPoints = [
          { id: '1', name: '函数与方程', accuracy: 45, subject: '数学' },
          { id: '2', name: '阅读理解', accuracy: 52, subject: '英语' },
          { id: '3', name: '电学基础', accuracy: 38, subject: '物理' }
        ];
      }
    },
    
    async loadRecentErrors() {
      try {
        const response = await axios.get('/student/recent-errors');
        const data = response.data.data;
        this.recentErrors = data.errors || [];
      } catch (error) {
        console.error('加载最近错题失败:', error);
        this.recentErrors = [
          { 
            id: '1', 
            subject: '数学', 
            knowledgePoint: '函数与方程', 
            imageUrl: '/img/error-1.png',
            status: 'unmastered',
            createTime: new Date()
          },
          { 
            id: '2', 
            subject: '英语', 
            knowledgePoint: '阅读理解', 
            imageUrl: '/img/error-2.png',
            status: 'mastered',
            createTime: new Date()
          }
        ];
      }
    },
    
    toggleTask(task) {
      task.completed = !task.completed;
      this.completedTasks = this.todayTasks.filter(t => t.completed).length;
      
      // 更新任务状态到服务器
      axios.put(`/student/tasks/${task.id}`, {
        completed: task.completed
      }).catch(error => {
        console.error('更新任务状态失败:', error);
        task.completed = !task.completed; // 回滚状态
        this.completedTasks = this.todayTasks.filter(t => t.completed).length;
      });
    },
    
    goToErrorBook() {
      this.$router.push('/student/error-book');
    },
    
    goToPractice() {
      this.$router.push('/student/practice');
    },
    
    goToPlanning() {
      this.$router.push('/student/planning');
    },
    
    goToPrint() {
      this.$router.push('/student/real-time-answers');
    },
    
    viewErrorDetail(error) {
      this.$router.push(`/student/error-detail/${error.id}`);
    },
    
    formatTime(time) {
      return moment(time).format('MM-DD HH:mm');
    }
  }
}
</script>

<style lang="less" scoped>
.student-dashboard {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

// 统计卡片
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  i {
    font-size: 24px;
    color: white;
  }
  
  &.error-icon {
    background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  }
  
  &.progress-icon {
    background: linear-gradient(135deg, #4ecdc4, #44a08d);
  }
  
  &.target-icon {
    background: linear-gradient(135deg, #45b7d1, #96c93d);
  }
  
  &.study-icon {
    background: linear-gradient(135deg, #f093fb, #f5576c);
  }
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
}

// 主要内容区域
.main-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.left-panel, .right-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// 通用区块样式
.progress-section, .today-tasks, .weak-points, .quick-actions, .recent-errors {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.view-all {
  color: #3498db;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    color: #2980b9;
  }
}

// 学习进度
.progress-percentage {
  font-size: 16px;
  font-weight: 600;
  color: #27ae60;
}

.progress-bar {
  height: 8px;
  background-color: #ecf0f1;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #27ae60, #2ecc71);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-info {
  font-size: 14px;
  color: #7f8c8d;
}

// 今日任务
.task-count {
  background-color: #3498db;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
  
  &.completed {
    opacity: 0.6;
    
    .task-title {
      text-decoration: line-through;
    }
  }
}

.task-checkbox {
  margin-right: 12px;
  
  i {
    font-size: 18px;
    color: #27ae60;
  }
}

.task-content {
  flex: 1;
}

.task-title {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.task-desc {
  font-size: 12px;
  color: #7f8c8d;
}

// 薄弱知识点
.weak-point-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.weak-point-item {
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #e74c3c;
}

.point-name {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 8px;
}

.point-accuracy {
  display: flex;
  align-items: center;
  gap: 8px;
}

.accuracy-text {
  font-size: 12px;
  color: #7f8c8d;
  min-width: 40px;
}

.accuracy-bar {
  flex: 1;
  height: 6px;
  background-color: #ecf0f1;
  border-radius: 3px;
  overflow: hidden;
}

.accuracy-fill {
  height: 100%;
  background: linear-gradient(90deg, #e74c3c, #c0392b);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.accuracy-value {
  font-size: 12px;
  font-weight: 600;
  color: #e74c3c;
  min-width: 35px;
  text-align: right;
}

// 快速功能
.action-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.action-card {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e9ecef;
    transform: translateY(-2px);
  }
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  
  i {
    font-size: 20px;
    color: white;
  }
  
  &.error-book-icon {
    background: linear-gradient(135deg, #667eea, #764ba2);
  }
  
  &.practice-icon {
    background: linear-gradient(135deg, #f093fb, #f5576c);
  }
  
  &.planning-icon {
    background: linear-gradient(135deg, #4facfe, #00f2fe);
  }
  
  &.print-icon {
    background: linear-gradient(135deg, #43e97b, #38f9d7);
  }
}

.action-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.action-desc {
  font-size: 12px;
  color: #7f8c8d;
}

// 最近错题
.error-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
}

.error-image {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 12px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.error-content {
  flex: 1;
}

.error-subject {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.error-knowledge {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.error-time {
  font-size: 11px;
  color: #95a5a6;
}

.error-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  
  &.mastered {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.unmastered {
    background-color: #f8d7da;
    color: #721c24;
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .student-dashboard {
    padding: 15px;
  }
  
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .stat-card {
    padding: 16px;
  }
  
  .stat-icon {
    width: 48px;
    height: 48px;
    margin-right: 12px;
    
    i {
      font-size: 20px;
    }
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .action-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style>
